import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AppPermissionService } from '@core/services/app-permission.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { PaymentGatewayBotDataService } from '../service/payment-gateway-bot-data.service';

@Component({
  selector: 'kt-pg-reconciliation-job-create',
  templateUrl: './pg-reconciliation-job-create.component.html',
  styleUrls: ['./pg-reconciliation-job-create.component.scss']
})
export class PGReconciliationJobCreateComponent implements OnInit, OnDestroy {
    form: FormGroup;
    buttonLoading = false;
    ranges: any;
    messages$ = this.paymentGatewayBotDataService.messages$;

    // permissions
    canCreateReconciliationJob: boolean;

    defaultStart = this.transactionHttpService.getYesterday().from;
    defaultEnd = this.transactionHttpService.getYesterday().to;

    paymentGatewayDropdownSettings = {};
    paymentGatewayDropdownList = [];
    paymentGatewaySelectedItems = [];
    paymentGatewayAccountLoading = false;
    currencies = (JSON.parse(sessionStorage.getItem('currencies')) || []).map((currency: any) => currency.name);

    private subscriptions: Subscription[] = [];
    private paymentGatewayAccountSub: Subscription;
    private isDestroyed = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {},
        public dialogRef: MatDialogRef<PGReconciliationJobCreateComponent>,
        private appPermissionService: AppPermissionService,
        private transactionHttpService: TransactionHttpService,
        private paymentGatewayBotDataService: PaymentGatewayBotDataService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.paymentGatewayDropdownSettings = {
            singleSelection: false,
            text: 'Please Select',
            maxHeight: 200,
            enableFilterSelectAll: false,
            enableSearchFilter: true,
            classes: 'dropdown',
            primaryKey: 'id',
            labelKey: 'displayLabel',
            lazyLoading: false,
            noDataLabel: '',
            showCheckbox: false,
            autoPosition: false,
            disabled: false
        };

        this.ranges = this.transactionHttpService.ranges;
        this.formInit();
        this.refreshPaymentGatewayAccounts();

        const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
            this.canCreateReconciliationJob = appPermissions.create_reconciliation_job;
        });

        this.subscriptions.push(apSub);
    }

    ngOnDestroy() {
        this.isDestroyed = true;
        if (this.paymentGatewayAccountSub) {
            this.paymentGatewayAccountSub.unsubscribe();
        }
        this.subscriptions.forEach(sb => sb.unsubscribe());
    }

    onCloseDialog(value = false) {
        this.dialogRef.close(value);
    }

    onSave() {
        this.buttonLoading = true;
        // To set "Save" button to disable (To prevent call API in multiple times when double click)
        this.form.setErrors({ 'invalid': true });
        const data = {
        ...this.form.value
        }

        Object.keys(data).forEach((key) => key != 'defaultDate' && (data[key] == null || data[key] === '') && delete data[key]);
        delete data.currency_code;
        
        this.paymentGatewayBotDataService.createReconJob(data).pipe(
            tap((res: any) => {
                this.buttonLoading = false;
            }),
            catchError((error) => {
                this.buttonLoading = false;
                this.form.setErrors(null);
                throw error;
            })
        ).subscribe(); 
    }

    refreshPaymentGatewayAccounts(currencyCode = this.form.value.currency_code) {
        this.paymentGatewaySelectedItems = [];
        this.form.patchValue({ pg_bot_id: null }, { emitEvent: false });

        const currencyParam = currencyCode ? `&currency_code=${encodeURIComponent(currencyCode)}` : '';
        if (this.paymentGatewayAccountSub) {
            this.paymentGatewayAccountSub.unsubscribe();
        }

        this.paymentGatewayAccountLoading = true;
        this.cdr.detectChanges();

        this.paymentGatewayAccountSub = this.paymentGatewayBotDataService.getWithQuery(`?limit=0&switch=1&status=1&reconciliation=1${currencyParam}`).pipe(
            finalize(() => {
                this.paymentGatewayAccountLoading = false;
                if (!this.isDestroyed) {
                    this.cdr.detectChanges();
                }
            })
        ).subscribe(res => {
            this.paymentGatewayDropdownList = res.map(data => ({
                id: data.id,
                displayLabel: `${data.payment_gateway_code} - ${data.username}`
            }));
        });
    }

    private formInit() {  
        this.form = new FormGroup({
            defaultDate: new FormControl({value: {startDate: this.defaultStart, endDate: this.defaultEnd}, disabled: true}),
            currency_code: new FormControl(''),
            pg_bot_id: new FormControl(null, [Validators.required]),
        });

        const currencySub = this.form.get('currency_code').valueChanges.subscribe(currencyCode => {
            this.refreshPaymentGatewayAccounts(currencyCode);
        });

        this.subscriptions.push(currencySub);
    }
}
